import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type { CreateGateEntryRequest, GateEntryDto, GateEntryLineDto, GateEntryLineInput, PurchaseOrderDto, UpdateGateEntryRequest } from '@/types/purchasing';

const emptyLine: GateEntryLineInput = { materialId: '', qty: 0, remarks: null };

export default function GateEntriesPage() {
  const { t } = useTranslation('purchasing');
  const queryClient = useQueryClient();
  const { data: config } = useProcessConfiguration();

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['gate-entries'],
    queryFn: async () => (await apiClient.get<GateEntryDto[]>('/gate-entries')).data,
  });
  const { data: orders = [] } = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: async () => (await apiClient.get<PurchaseOrderDto[]>('/purchase-orders')).data,
    enabled: config?.purchaseOrderEnabled ?? false,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];

  // Still-unlogged balance per PO line: `orderedQty - receivedQty` alone isn't enough since
  // PurchaseOrderLine.ReceivedQty only reflects GRN receipt, not what's already been copied into an
  // earlier (still-pending) Gate Entry against this same PO — so first subtract whatever qty other
  // gate entries already logged per material. Shared by the PO picker's visibility filter and
  // applyPurchaseOrder so the two never disagree.
  const getPoGateEntryBalanceLines = (po: PurchaseOrderDto) => {
    const alreadyLoggedByMaterial = new Map<string, number>();
    for (const otherEntry of entries) {
      if (otherEntry.purchaseOrderId !== po.id) continue;
      for (const line of otherEntry.lines) {
        alreadyLoggedByMaterial.set(line.materialId, (alreadyLoggedByMaterial.get(line.materialId) ?? 0) + line.qty);
      }
    }
    return po.lines
      .map((l) => ({ materialId: l.materialId, qty: l.orderedQty - (alreadyLoggedByMaterial.get(l.materialId) ?? 0), remarks: null }))
      .filter((l) => l.qty > 0);
  };

  // Only an Approved PO not yet fully received (and not manually closed), with something still
  // unlogged, can be logged in at the gate — once every line is received (Status flips to
  // "Received"), closed, or already fully copied into other gate entries, it drops out of this list.
  const openOrders = orders.filter(
    (o) => (o.status === 'Approved' || o.status === 'PartiallyReceived') && !o.isClosed && getPoGateEntryBalanceLines(o).length > 0,
  );

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<GateEntryDto | null>(null);
  const [purchaseOrderId, setPurchaseOrderId] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [driverName, setDriverName] = useState('');
  const [inwardDocNo, setInwardDocNo] = useState('');
  const [lines, setLines] = useState<GateEntryLineInput[]>([{ ...emptyLine }]);

  const resetForm = () => {
    setEditingId(null);
    setPurchaseOrderId('');
    setVehicleNo('');
    setDriverName('');
    setInwardDocNo('');
    setLines([{ ...emptyLine }]);
  };

  // Selecting a Purchase Order (optional) copies its still-unlogged balance in as editable
  // gate-entry items — quantities can be changed and lines added/removed before raising the entry.
  const applyPurchaseOrder = (poId: string) => {
    setPurchaseOrderId(poId);
    const po = orders.find((o) => o.id === poId);
    if (!po) return;

    const balanceLines = getPoGateEntryBalanceLines(po);
    setLines(balanceLines.length > 0 ? balanceLines : [{ ...emptyLine }]);
  };

  // Only usable up to the point receiving actually starts — mirrors GateEntryService.UpdateAsync's guard.
  const canEdit = (r: GateEntryDto) => !r.isClosed && r.lines.every((l) => l.receivedQty <= 0);

  const openEdit = (r: GateEntryDto) => {
    setEditingId(r.id);
    setPurchaseOrderId('');
    setVehicleNo(r.vehicleNo);
    setDriverName(r.driverName ?? '');
    setInwardDocNo(r.inwardDocNo ?? '');
    setLines(r.lines.map((l) => ({ materialId: l.materialId, qty: l.qty, remarks: l.remarks })));
    setOpen(true);
  };

  const updateLine = (idx: number, patch: Partial<GateEntryLineInput>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const createMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.materialId);
      if (editingId) {
        const payload: UpdateGateEntryRequest = { vehicleNo, driverName: driverName || null, inwardDocNo: inwardDocNo || null, lines: cleanLines };
        return apiClient.put(`/gate-entries/${editingId}`, payload);
      }
      const payload: CreateGateEntryRequest = {
        purchaseOrderId: purchaseOrderId || null, vehicleNo, driverName: driverName || null, inwardDocNo: inwardDocNo || null,
        lines: cleanLines,
      };
      return apiClient.post('/gate-entries', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gate-entries'] });
      setOpen(false);
    },
  });

  const closeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/gate-entries/${id}/close`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gate-entries'] }),
  });

  const columns: DataTableColumn<GateEntryDto>[] = [
    { field: 'gateEntryNo', headerName: t('gateEntry.table.number') },
    { field: 'vehicleNo', headerName: t('gateEntry.table.vehicle') },
    { field: 'driverName', headerName: t('gateEntry.table.driver'), render: (r) => r.driverName ?? '—' },
    ...(config?.purchaseOrderEnabled
      ? [{ field: 'purchaseOrderNumber', headerName: t('gateEntry.table.po'), render: (r: GateEntryDto) => r.purchaseOrderNumber ?? '—' } as DataTableColumn<GateEntryDto>]
      : []),
    { field: 'lines', headerName: t('form.items'), render: (r) => r.lines.length },
    { field: 'entryDateTime', headerName: t('gateEntry.table.entry'), render: (r) => new Date(r.entryDateTime).toLocaleString() },
    { field: 'status', headerName: t('gateEntry.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: t('gateEntry.table.actions'), render: (r) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" onClick={() => setViewing(r)}>
            {t('view')}
          </Button>
          <IfPermitted permission={PERMISSIONS.GATE_ENTRY_MANAGE}>
            {canEdit(r) && (
              <Button size="small" onClick={() => openEdit(r)}>
                {t('edit')}
              </Button>
            )}
            {r.isClosed ? (
              <StatusChip status="Closed" />
            ) : r.isFullyReceived ? (
              <StatusChip status="Received" />
            ) : r.lines.some((l) => l.receivedQty > 0) ? (
              // Same "stalled partial receipt only" rule as Purchase Order — nothing received
              // yet means nothing to retire, so Close stays hidden until GRN has posted something
              // against at least one line.
              <Tooltip title={t('gateEntry.closeHint')}>
                <Checkbox size="small" disabled={closeMutation.isPending} onChange={() => closeMutation.mutate(r.id)} />
              </Tooltip>
            ) : null}
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const lineColumns: DataTableColumn<GateEntryLineDto>[] = [
    { field: 'materialCode', headerName: t('gateEntry.table.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'qty', headerName: t('gateEntry.table.qty') },
    { field: 'receivedQty', headerName: t('gateEntry.table.receivedQty') },
    { field: 'remarks', headerName: t('gateEntry.table.remarks'), render: (l) => l.remarks ?? '—' },
  ];

  return (
    <>
      <PageHeader
        title={t('gateEntry.title')}
        subtitle={t('gateEntry.subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.GATE_ENTRY_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              {t('gateEntry.add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={entries} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="gate-entries" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('edit') : t('gateEntry.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!editingId && config?.purchaseOrderEnabled && (
              <SearchableSelect
                label={t('form.linkedPO')} value={purchaseOrderId} onChange={applyPurchaseOrder} fullWidth
                options={[
                  { value: '', label: t('form.none') },
                  ...openOrders.map((o) => ({ value: o.id, label: `${o.poNumber} — ${o.supplierName}` })),
                ]}
              />
            )}

            <Stack direction="row" spacing={2}>
              <TextField label={t('form.vehicleNo')} value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} fullWidth />
              <TextField label={t('form.driverName')} value={driverName} onChange={(e) => setDriverName(e.target.value)} fullWidth />
            </Stack>
            <TextField label={t('form.inwardDocNo')} value={inwardDocNo} onChange={(e) => setInwardDocNo(e.target.value)} fullWidth />

            <Divider />
            <Typography variant="subtitle2">{t('form.items')}</Typography>

            {lines.map((line, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <SearchableSelect
                  label={t('form.material')} value={line.materialId} fullWidth size="small"
                  onChange={(materialId) => updateLine(idx, { materialId })}
                  options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                />
                <TextField
                  type="number" label={t('form.quantity')} value={line.qty} size="small" sx={{ minWidth: 110 }}
                  onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })}
                />
                <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLines((prev) => [...prev, { ...emptyLine }])}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={!vehicleNo || lines.some((l) => !l.materialId || l.qty <= 0) || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.gateEntryNo ?? ''}
        subtitle={viewing ? `${viewing.vehicleNo}${viewing.driverName ? ` — ${viewing.driverName}` : ''}` : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />
    </>
  );
}
